/**
 * Deployment Token Validation Schemas
 *
 * Validation schemas for deployment token operations including creation.
 *
 * CRITICAL: This file is adapted from tenant-api/src/validators/deployment-token.ts
 * Note: Uses local enum imports instead of backend models
 */

import { z } from 'zod';

// ============================================================================
// TOKEN CODE VALIDATION
// ============================================================================

/**
 * Token code validation schema
 * Format: lowercase alphanumeric with hyphens (e.g., "ci-prod", "github-actions")
 * Must be unique within an application
 */
export const tokenCodeSchema = z
  .string({
    required_error: 'Token code is required',
    invalid_type_error: 'Token code must be a string',
  })
  .min(2, 'Token code must be at least 2 characters')
  .max(50, 'Token code must not exceed 50 characters')
  .regex(
    /^[a-z][a-z0-9-]*[a-z0-9]$|^[a-z]$/,
    'Token code must be lowercase, start with a letter, and contain only letters, numbers, and hyphens'
  );

export type TokenCodeValidation = z.infer<typeof tokenCodeSchema>;

// ============================================================================
// TOKEN NAME VALIDATION
// ============================================================================

/**
 * Token name validation schema
 * Human-readable display name for the token
 */
export const tokenNameSchema = z
  .string({
    required_error: 'Token name is required',
    invalid_type_error: 'Token name must be a string',
  })
  .min(1, 'Token name must be at least 1 character')
  .max(100, 'Token name must not exceed 100 characters')
  .trim();

export type TokenNameValidation = z.infer<typeof tokenNameSchema>;

// ============================================================================
// EXPIRATION VALIDATION
// ============================================================================

/**
 * Token expiration days validation schema
 * Must be between 1 and 365 days
 * Default: 30 days (handled by backend)
 */
export const expiresInDaysSchema = z
  .number({
    invalid_type_error: 'Expiration days must be a number',
  })
  .int('Expiration days must be a whole number')
  .min(1, 'Expiration must be at least 1 day')
  .max(365, 'Expiration must not exceed 365 days')
  .optional();

export type ExpiresInDaysValidation = z.infer<typeof expiresInDaysSchema>;

// ============================================================================
// DEPLOYMENT TOKEN CREATE VALIDATION
// ============================================================================

/**
 * Deployment token creation schema
 * Used when creating a new deployment token
 *
 * Required fields:
 * - tokenCode: Unique code within application (used in CI/CD config)
 * - tokenName: Human-readable display name
 *
 * Optional fields:
 * - expiresInDays: Days until expiration (1-365, default 30)
 */
export const deploymentTokenCreateSchema = z
  .object({
    tokenCode: tokenCodeSchema,
    tokenName: tokenNameSchema,
    expiresInDays: expiresInDaysSchema,
  })
  .strict();

export type DeploymentTokenCreateValidation = z.infer<typeof deploymentTokenCreateSchema>;
